import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaClock, FaBriefcase, FaDollarSign, FaCalendar, FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import ApplyModal from '../components/ApplyModal';

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);

  useEffect(() => {
    fetchJobDetails();
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${id}`);
      setJob(response.data.job);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch job details');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h2>
          <button
            onClick={() => navigate('/careers')}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-2 mx-auto"
          >
            <FaArrowLeft /> Back to Careers
          </button>
        </div>
      </div>
    );
  }

  // Google Jobs structured data for single job
  const jobJSONLD = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "datePosted": job.postedDate,
    "validThrough": job.deadline,
    "employmentType": job.type.toUpperCase(),
    "hiringOrganization": {
      "@type": "Organization",
      "name": "Versaimmortal Infotech",
      "sameAs": "https://versaimmortal.com"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location
      }
    },
    "estimatedSalary": job.salary ? {
      "@type": "MonetaryAmount",
      "currency": job.salary.currency,
      "value": {
        "@type": "QuantitativeValue",
        "minValue": job.salary.min,
        "maxValue": job.salary.max,
        "unitText": "YEAR"
      }
    } : undefined
  };

  return (
    <>
      <Helmet>
        <title>{job.title} - Career at Versaimmortal Infotech</title>
        <meta name="description" content={job.description.substring(0, 160)} />
        <meta property="og:title" content={`${job.title} - Versaimmortal Infotech`} />
        <meta property="og:description" content={job.description.substring(0, 160)} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://versaimmortal.com/careers/${id}`} />
        
        {/* Google Jobs structured data */}
        <script type="application/ld+json">
          {JSON.stringify(jobJSONLD)}
        </script>
      </Helmet>

      {/* Back Button */}
      <div className="bg-gray-100 py-4">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate('/careers')}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
          >
            <FaArrowLeft /> Back to Careers
          </button>
        </div>
      </div>

      {/* Job Details */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white p-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">{job.title}</h1>
              
              <div className="flex flex-wrap gap-4">
                <span className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                  <FaBriefcase />
                  {job.department}
                </span>
                <span className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                  <FaMapMarkerAlt />
                  {job.location}
                </span>
                <span className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                  <FaClock />
                  {job.type.charAt(0).toUpperCase() + job.type.slice(1)}
                </span>
                {job.salary && (
                  <span className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                    <FaDollarSign />
                    {job.salary.currency} {job.salary.min} - {job.salary.max}
                  </span>
                )}
                {job.deadline && (
                  <span className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full">
                    <FaCalendar />
                    Apply by: {new Date(job.deadline).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Description */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">Job Description</h2>
                <p className="text-gray-700 leading-relaxed">{job.description}</p>
              </div>

              {/* Responsibilities */}
              {job.responsibilities && job.responsibilities.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-4">Responsibilities</h2>
                  <ul className="list-disc list-inside space-y-2">
                    {job.responsibilities.map((item, index) => (
                      <li key={index} className="text-gray-700">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Requirements */}
              {job.requirements && job.requirements.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-4">Requirements</h2>
                  <ul className="list-disc list-inside space-y-2">
                    {job.requirements.map((item, index) => (
                      <li key={index} className="text-gray-700">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Qualifications */}
              {job.qualifications && job.qualifications.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-4">Qualifications</h2>
                  <ul className="list-disc list-inside space-y-2">
                    {job.qualifications.map((item, index) => (
                      <li key={index} className="text-gray-700">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Experience */}
              {job.experience && (job.experience.min || job.experience.max) && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-4">Experience Required</h2>
                  <p className="text-gray-700">
                    {job.experience.min && job.experience.max
                      ? `${job.experience.min} - ${job.experience.max} years`
                      : job.experience.min
                      ? `Minimum ${job.experience.min} years`
                      : `Up to ${job.experience.max} years`}
                  </p>
                </div>
              )}

              {/* Apply Button */}
              <div className="text-center">
                <button
                  onClick={() => setShowApplyModal(true)}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-300 text-lg"
                >
                  Apply for this Position
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Apply Modal */}
      <ApplyModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        job={job}
      />
    </>
  );
};

export default JobDetails;